/**
 * C · Product recommendations — source tabs, related fetch, c-pl slider
 */

function getActiveTrack(section) {
  const panel = section.querySelector('.c-pr__panel.is-active');
  return panel?.querySelector('.c-pl__track');
}

function getStep(track) {
  const slides = [...track.querySelectorAll('.c-pl__slide')];
  if (slides.length < 2) return track.clientWidth;
  const a = slides[0].getBoundingClientRect();
  const b = slides[1].getBoundingClientRect();
  return Math.abs(b.left - a.left) || a.width || track.clientWidth;
}

function normalizedScroll(track) {
  const dir = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
  return {
    dir,
    pos: track.scrollLeft * dir,
    max: Math.max(0, track.scrollWidth - track.clientWidth),
  };
}

function moveTrack(track, intent) {
  const { dir } = normalizedScroll(track);
  const sign = (intent === 'next' ? 1 : -1) * dir;
  track.scrollBy({ left: sign * getStep(track), behavior: 'smooth' });
}

function updateNavButtons(section) {
  if (section.classList.contains('c-pl--grid')) return;

  const track = getActiveTrack(section);
  const prevBtn = section.querySelector('.c-pl__nav-btn[data-dir="prev"]');
  const nextBtn = section.querySelector('.c-pl__nav-btn[data-dir="next"]');
  if (!track || !prevBtn || !nextBtn) return;

  const { pos, max } = normalizedScroll(track);
  if (max <= 4) {
    prevBtn.disabled = true;
    nextBtn.disabled = true;
    return;
  }

  prevBtn.disabled = pos <= 4;
  nextBtn.disabled = pos >= max - 4;
}

function bindNav(section) {
  if (section.dataset.cPrNavBound === 'true' || section.classList.contains('c-pl--grid')) return;
  section.dataset.cPrNavBound = 'true';

  const prevBtn = section.querySelector('.c-pl__nav-btn[data-dir="prev"]');
  const nextBtn = section.querySelector('.c-pl__nav-btn[data-dir="next"]');
  if (!prevBtn || !nextBtn) return;

  prevBtn.addEventListener('click', () => {
    const track = getActiveTrack(section);
    if (track) moveTrack(track, 'prev');
  });

  nextBtn.addEventListener('click', () => {
    const track = getActiveTrack(section);
    if (track) moveTrack(track, 'next');
  });

  section.addEventListener(
    'scroll',
    (event) => {
      if (event.target.classList?.contains('c-pl__track')) {
        updateNavButtons(section);
      }
    },
    true
  );

  window.addEventListener('resize', () => updateNavButtons(section));
}

function initTabs(section) {
  const tabs = [...section.querySelectorAll('[data-c-pr-tab]')];
  const panels = [...section.querySelectorAll('[data-c-pr-panel]')];
  const viewAll = section.querySelector('[data-c-pr-view-all]');
  if (!tabs.length || !panels.length) return;

  const activate = (source) => {
    tabs.forEach((tab) => {
      const isActive = tab.dataset.cPrTab === source;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    panels.forEach((panel) => {
      const isActive = panel.dataset.cPrPanel === source;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
      if (isActive) {
        panel.querySelector('.c-pl__track')?.scrollTo({ left: 0 });
      }
    });

    if (viewAll) {
      const activeTab = tabs.find((tab) => tab.dataset.cPrTab === source);
      if (activeTab?.dataset.viewAllUrl) {
        viewAll.href = activeTab.dataset.viewAllUrl;
        viewAll.hidden = false;
      } else {
        viewAll.hidden = true;
      }
    }

    updateNavButtons(section);
  };

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      if (tab.classList.contains('is-active')) return;
      activate(tab.dataset.cPrTab);
    });
  });

  const defaultTab = tabs.find((tab) => tab.classList.contains('is-active')) || tabs[0];
  if (defaultTab) activate(defaultTab.dataset.cPrTab);
}

async function loadRelated(section) {
  if (section.dataset.cPrRelatedLoaded === 'true') return;

  const panel = section.querySelector('[data-c-pr-panel="related"]');
  if (!panel || panel.dataset.cPrNeedsFetch !== 'true') return;

  const { recommendationsUrl, productId, sectionId } = section.dataset;
  if (!recommendationsUrl || !productId || !sectionId) return;

  const url = `${recommendationsUrl}&product_id=${productId}&section_id=${sectionId}&intent=related`;

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fetchedPanel = doc.querySelector('[data-c-pr-panel="related"]');

    if (fetchedPanel) {
      panel.innerHTML = fetchedPanel.innerHTML;
      panel.removeAttribute('data-c-pr-needs-fetch');
      section.dataset.cPrRelatedLoaded = 'true';

      if (fetchedPanel.dataset.cPrEmpty === 'true') {
        panel.dataset.cPrEmpty = 'true';
        const relatedTab = section.querySelector('[data-c-pr-tab="related"]');
        relatedTab?.setAttribute('hidden', '');
        const nextTab = section.querySelector('[data-c-pr-tab]:not([hidden])');
        if (nextTab) nextTab.click();
      }

      updateNavButtons(section);
    }
  } catch (error) {
    console.error('Product recommendations:', error);
    panel.querySelector('.c-pl__track').innerHTML = '';
    panel.dataset.cPrEmpty = 'true';
  }
}

function initRecommendations(section) {
  if (!section || section.dataset.cPrReady === 'true') return;
  section.dataset.cPrReady = 'true';

  bindNav(section);
  initTabs(section);

  const relatedPanel = section.querySelector('[data-c-pr-panel="related"]');
  const needsFetch = relatedPanel?.dataset.cPrNeedsFetch === 'true';

  if (needsFetch) {
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        loadRelated(section);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    observer.observe(section);
  } else {
    updateNavButtons(section);
  }
}

function initAll(root = document) {
  const scope = root instanceof Element ? root : document;
  scope.querySelectorAll('[data-c-pr]').forEach(initRecommendations);
}

initAll(document);
document.addEventListener('shopify:section:load', (event) => initAll(event.target));

export { initRecommendations };
